import React, { useEffect, useRef } from "react";
import { Toast } from "./Toast";

export interface ToastNotification {
  id: string;
  title: string;
  message: string;
  duration?: number;
  onClick?: () => void;
}

interface ToastContainerProps {
  notifications: ToastNotification[];
  onRemove: (id: string) => void;
}

interface ToastTimerState {
  timerId: NodeJS.Timeout | null;
  remainingTime: number;
  startTime: number;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  notifications,
  onRemove,
}) => {
  const timersRef = useRef<Map<string, ToastTimerState>>(new Map());

  useEffect(() => {
    notifications.forEach((notification) => {
      if (!timersRef.current.has(notification.id)) {
        const duration = notification.duration || 5000;
        const timerId = setTimeout(() => {
          onRemove(notification.id);
          timersRef.current.delete(notification.id);
        }, duration);

        timersRef.current.set(notification.id, {
          timerId,
          remainingTime: duration,
          startTime: Date.now(),
        });
      }
    });

    const currentIds = new Set(notifications.map((n) => n.id));
    timersRef.current.forEach((timer, id) => {
      if (!currentIds.has(id)) {
        if (timer.timerId) clearTimeout(timer.timerId);
        timersRef.current.delete(id);
      }
    });

    return () => {
      timersRef.current.forEach((timer) => {
        if (timer.timerId) clearTimeout(timer.timerId);
      });
      timersRef.current.clear();
    };
  }, [notifications, onRemove]);

  const handleMouseEnter = (id: string) => {
    const timer = timersRef.current.get(id);
    if (timer && timer.timerId) {
      clearTimeout(timer.timerId);
      const elapsed = Date.now() - timer.startTime;
      const remaining = Math.max(0, timer.remainingTime - elapsed);
      timersRef.current.set(id, {
        ...timer,
        timerId: null,
        remainingTime: remaining,
      });
    }
  };

  const handleMouseLeave = (id: string) => {
    const timer = timersRef.current.get(id);
    if (timer && !timer.timerId) {
      const timerId = setTimeout(() => {
        onRemove(id);
        timersRef.current.delete(id);
      }, timer.remainingTime);

      timersRef.current.set(id, {
        ...timer,
        timerId,
        startTime: Date.now(),
      });
    }
  };

  return (
    <>
      {notifications.map((notification, index) => (
        <div
          key={notification.id}
          style={{
            position: "fixed",
            top: 20 + index * 100,
            right: 20,
            zIndex: 9999,
          }}
          onMouseEnter={() => handleMouseEnter(notification.id)}
          onMouseLeave={() => handleMouseLeave(notification.id)}
        >
          <Toast
            title={notification.title}
            message={notification.message}
            onClose={() => onRemove(notification.id)}
            onClick={notification.onClick}
          />
        </div>
      ))}
    </>
  );
};
